import React from 'react';
import styled from 'styled-components';
import { Icon, palette } from '@m12s/component-library';
import { useHistory } from 'react-router-dom';

import { farTicketAlt } from 'lib/icons';
import { Small, Text } from 'components/Text';
import { toISO } from 'lib/utils/date';
import {
  CREATE_ACTIVITY,
  STOP_OPERATOR,
  UPDATE_ACTIVITY,
  UPDATE_ACTIVITY_SET,
} from 'lib/api/mutations';
import { useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { useTranslation } from 'react-i18next';
import { getLatestJobName } from 'lib/selectors/getLatestJobName';
import { getLaborTicketAction } from 'lib/selectors/getLaborTicketAction';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { getMachine } from 'lib/selectors/getMachine';
import { getPendingActivityTypeRef } from 'lib/selectors/getPendingActivityTypeRef';
import { useCreateActivityRequestParams } from 'lib/hooks';
import { getActivityTypes } from 'lib/selectors/getActivityTypes';

import Card from './Card';
import { ModalButton } from './styled';
import {
  getCreateActivityOptions,
  getRequestUpdateActivityOptions,
  getRequestUpdateActivitySetOptions,
  getStopOperatorOptions,
  stopJob,
} from './helpers';

const BodyContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const LabelAndFieldContainer = styled.div`
  display: grid;
`;

const Label = styled(Small)`
  font-weight: 400;
`;

const Field = styled(Text)`
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  gap: 0.5rem;

  > * {
    min-width: 38px;
  }
`;

const LaborTicketCard = ({ form }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const latestActivitySet = useSelector(getLatestActivitySet);
  const latestActivity = useSelector(getLatestActivity);
  const { t } = useTranslation();
  const latestJobName = useSelector(getLatestJobName);
  const jobName = t(latestJobName);
  const laborTicketAction = useSelector(getLaborTicketAction);
  const machine = useSelector(getMachine);
  const pendingActivityTypeRef = useSelector(getPendingActivityTypeRef);
  const createActivityRequestParams = useCreateActivityRequestParams();
  const activityTypes = useSelector(getActivityTypes);

  const pendingActivityTypeName = activityTypes.find(({ activityTypeRef }) => {
    return activityTypeRef === pendingActivityTypeRef;
  })?.name;

  // Update Activity Set
  const requestUpdateActivitySetOptions = getRequestUpdateActivitySetOptions({
    dispatch,
    latestActivitySet,
    latestActivity,
    t,
    jobName,
  });

  const [requestUpdateActivitySet] = useMutation(
    UPDATE_ACTIVITY_SET,
    requestUpdateActivitySetOptions
  );

  // Stop Operator
  const stopOperatorOptions = getStopOperatorOptions({
    history,
    machine,
    dispatch,
  });
  const [stopOperator] = useMutation(STOP_OPERATOR, stopOperatorOptions);

  // Create Activity
  const createActivityOptions = getCreateActivityOptions({
    dispatch,
    t,
    pendingActivityTypeName,
  });
  const [requestCreateActivity] = useMutation(
    CREATE_ACTIVITY,
    createActivityOptions
  );

  // Pause
  const updateActivityOptions = getRequestUpdateActivityOptions({
    dispatch,
    t,
    latestActivity,
    history,
    machine,
  });
  const [requestUpdateActivity] = useMutation(
    UPDATE_ACTIVITY,
    updateActivityOptions
  );

  return (
    <Card
      header={
        <>
          <Icon icon={farTicketAlt} style={{ marginRight: '0.5rem' }} />
          Labor Ticket
        </>
      }
      body={
        <BodyContainer>
          <ColumnContainer>
            <LabelAndFieldContainer>
              <Label>Operator</Label>
              <Field>Jordan Mitchell</Field>
            </LabelAndFieldContainer>

            <LabelAndFieldContainer>
              <Label>Work order</Label>
              <Field>12345-005</Field>
            </LabelAndFieldContainer>

            <Row>
              <LabelAndFieldContainer>
                <Label>Lot</Label>
                <Field>1</Field>
              </LabelAndFieldContainer>

              <LabelAndFieldContainer>
                <Label>Split</Label>
                <Field>0</Field>
              </LabelAndFieldContainer>

              <LabelAndFieldContainer>
                <Label>Sub</Label>
                <Field>0</Field>
              </LabelAndFieldContainer>
            </Row>

            <LabelAndFieldContainer>
              <Label>Part Operation</Label>
              <Field>Wheel Bearing Op 10</Field>
            </LabelAndFieldContainer>

            <LabelAndFieldContainer>
              <Label>Order Progress</Label>
              <Field>32 / 2000</Field>
            </LabelAndFieldContainer>
          </ColumnContainer>

          <ColumnContainer>
            <LabelAndFieldContainer>
              <Label>Total Good Parts</Label>
              <Field>35</Field>
            </LabelAndFieldContainer>

            <LabelAndFieldContainer>
              <Label>Total Rejects</Label>
              <Field>5</Field>
            </LabelAndFieldContainer>

            <LabelAndFieldContainer>
              <Label>Started</Label>
              <Field>7/29/23 10:16 AM</Field>
            </LabelAndFieldContainer>

            <LabelAndFieldContainer>
              <Label>Ended</Label>
              <Field>Today 3.09 PM</Field>
            </LabelAndFieldContainer>

            <LabelAndFieldContainer>
              <Label>Activity</Label>
              <Field>Production</Field>
            </LabelAndFieldContainer>
          </ColumnContainer>
        </BodyContainer>
      }
      footer={
        <ModalButton
          customStyles={{
            background: palette.Indigo600,
          }}
          hoverStyles={{
            background: palette.Indigo700,
          }}
          onClick={() => {
            // eslint-disable-next-line no-console
            console.log('Submit', form);

            if (laborTicketAction === LABOR_TICKET_ACTIONS.STOP_RUN) {
              // eslint-disable-next-line no-console
              console.log('Stop job');
              return stopJob({
                requestUpdateActivitySet,
                latestActivitySet,
                latestActivity,
              });
            }
            if (laborTicketAction === LABOR_TICKET_ACTIONS.OPERATOR_LOGOUT) {
              // eslint-disable-next-line no-console
              return stopOperator({
                variables: {
                  input: {
                    machineId: machine.id,
                  },
                },
              });
            }
            if (laborTicketAction === LABOR_TICKET_ACTIONS.CHANGE_ACTIVITIES) {
              // eslint-disable-next-line no-console
              return requestCreateActivity(
                createActivityRequestParams(pendingActivityTypeRef)
              );
            }
            if (laborTicketAction === LABOR_TICKET_ACTIONS.PAUSE) {
              const now = toISO();
              return requestUpdateActivity({
                variables: {
                  input: {
                    activitySetRef: latestActivitySet.activitySetRef,
                    activityRef: latestActivity.activityRef,
                    endAt: now,
                  },
                },
                optimisticResponse: {
                  __typename: 'Mutation',
                  updateActivityResponse: {
                    __typename: 'Activity',
                    activity: {
                      ...latestActivity,
                      end: now,
                      updatedAt: now,
                      jobScheduleGroupId: null,
                    },
                  },
                },
              });
            }
            return null;
          }}
        >
          Submit to ERP
        </ModalButton>
      }
    />
  );
};

export default LaborTicketCard;
